import type { SpraySituationDetail } from '@/types/spray';

/**
 * The admin view of spray situation groups. The server owns the rules
 * (spraySituationGroups.js) and refuses a cycle; this only lays the graph out
 * and keeps the pickers from offering a choice the server would refuse.
 */

export interface SituationGroupNode {
  /** The canonical row naming this group. */
  row: SpraySituationDetail;
  /** Other spellings merged into it. */
  synonyms: SpraySituationDetail[];
  /** Narrower groups it covers. */
  children: SituationGroupNode[];
  /** Registrations filed under the group and all its spellings. */
  registrations: number;
}

function canonicalId(byId: Map<number, SpraySituationDetail>, id: number): number {
  const r = byId.get(id);
  return r?.same_as_id != null && byId.has(r.same_as_id) ? r.same_as_id : id;
}

/**
 * Lay the situations out as a forest of groups.
 *
 * `groups` are the top-level groups that have something in them — spellings
 * or narrower groups. `ungrouped` are the standalone rows still waiting to be
 * placed, which is where an admin looks first.
 */
export function buildSituationTree(rows: SpraySituationDetail[]): {
  groups: SituationGroupNode[];
  ungrouped: SpraySituationDetail[];
} {
  const byId = new Map(rows.map(r => [r.id, r]));
  const nodes = new Map<number, SituationGroupNode>();
  for (const r of rows) {
    if (canonicalId(byId, r.id) === r.id) {
      nodes.set(r.id, { row: r, synonyms: [], children: [], registrations: r.registration_count ?? 0 });
    }
  }
  for (const r of rows) {
    const c = canonicalId(byId, r.id);
    if (c !== r.id) {
      const node = nodes.get(c)!;
      node.synonyms.push(r);
      node.registrations += r.registration_count ?? 0;
    }
  }

  const roots: SituationGroupNode[] = [];
  for (const node of nodes.values()) {
    const p = node.row.parent_id;
    const parent = p != null && byId.has(p) ? nodes.get(canonicalId(byId, p)) : undefined;
    // A parent pointer that loops back is a data fault; show it as a root
    // rather than lose the row.
    if (parent && parent !== node && !isBelow(nodes, byId, parent.row.id, node.row.id)) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const byName = (a: SituationGroupNode, b: SituationGroupNode) => a.row.name.localeCompare(b.row.name);
  const sortDeep = (list: SituationGroupNode[]) => {
    list.sort(byName);
    for (const n of list) {
      n.synonyms.sort((a, b) => a.name.localeCompare(b.name));
      sortDeep(n.children);
    }
  };
  sortDeep(roots);

  return {
    groups: roots.filter(n => n.children.length || n.synonyms.length),
    ungrouped: roots.filter(n => !n.children.length && !n.synonyms.length).map(n => n.row),
  };
}

/** Is `id`'s group somewhere beneath `ancestorId`'s? */
function isBelow(
  nodes: Map<number, SituationGroupNode>,
  byId: Map<number, SpraySituationDetail>,
  id: number,
  ancestorId: number,
): boolean {
  const seen = new Set<number>();
  let cur: number | null = canonicalId(byId, id);
  while (cur != null && !seen.has(cur)) {
    seen.add(cur);
    const p: number | null = nodes.get(cur)?.row.parent_id ?? null;
    if (p == null || !byId.has(p)) return false;
    cur = canonicalId(byId, p);
    if (cur === canonicalId(byId, ancestorId)) return true;
  }
  return false;
}

/**
 * What `id` may be merged into, or placed under: anything outside its own
 * group and outside everything beneath it. Choosing one of those would make a
 * situation broader than itself.
 */
export function eligibleTargets(rows: SpraySituationDetail[], id: number): SpraySituationDetail[] {
  const byId = new Map(rows.map(r => [r.id, r]));
  const nodes = new Map<number, SituationGroupNode>();
  for (const r of rows) {
    if (canonicalId(byId, r.id) === r.id) nodes.set(r.id, { row: r, synonyms: [], children: [], registrations: 0 });
  }
  const own = canonicalId(byId, id);
  return rows
    .filter(r => {
      const c = canonicalId(byId, r.id);
      return c !== own && !isBelow(nodes, byId, c, own);
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
